import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TVSeriesCard from '@/components/feature/TVSeriesCard';
import SEOHead from '@/components/base/SEOHead';
import { tvSeries, categories, faqData } from '@/mocks/tvSeries';

const featuredSeries = tvSeries.filter((s) => s.featured);
const hotSeries = tvSeries.filter((s) => s.hot);
const latestSeries = [...tvSeries].sort((a, b) => b.year - a.year).slice(0, 8);
const topRated = [...tvSeries].sort((a, b) => b.rating - a.rating).slice(0, 6);

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % featuredSeries.length);
  }, [featuredSeries.length]);

  const prevBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev - 1 + featuredSeries.length) % featuredSeries.length);
  }, [featuredSeries.length]);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const currentFeature = featuredSeries[currentBanner];

  const homePageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '天美影视 - 最新电视剧全集高清免费在线观看',
    description: '天美影视电视剧大全，最新电视剧全集高清免费在线观看，不缓冲流畅追剧，免费看全集电视剧，天美影视剧集在线看。',
    url: 'https://www.tianmeitv.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '天美影视',
      url: 'https://www.tianmeitv.com',
    },
  };

  return (
    <>
      <SEOHead
        title="天美影视 | 天美影视电视剧大全，最新电视剧全集高清免费在线观看"
        description="天美影视电视剧大全，最新电视剧全集高清免费在线观看，不缓冲流畅追剧，免费看全集电视剧，天美影视剧集在线看。涵盖古装仙侠、都市情感、悬疑推理、科幻奇幻等海量电视剧资源。"
        keywords="天美影视,天美影视电视剧,电视剧大全,最新电视剧,全集在线看,天美影视剧集,高清电视剧,免费看电视剧,在线观看电视剧,不缓冲追剧"
        canonicalUrl="https://www.tianmeitv.com/"
        jsonLd={[homePageSchema]}
      />

      <Navbar />

      <main>
        {/* Hero Banner */}
        <section className="relative h-[420px] md:h-[600px] w-full overflow-hidden">
          <div className="absolute inset-0 flex transition-transform duration-700 ease-in-out">
            {featuredSeries.map((s, i) => (
              <div
                key={s.id}
                className="absolute inset-0 transition-opacity duration-700"
                style={{ opacity: i === currentBanner ? 1 : 0 }}
              >
                <img
                  src={s.banner}
                  alt={`${s.title} 天美影视热播推荐`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />

          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium text-background-50 bg-primary-500 px-2 py-0.5 rounded whitespace-nowrap">
                    热播推荐
                  </span>
                  <span className="text-xs text-white/70">{currentFeature.category}</span>
                  <span className="text-xs text-white/70">&middot;</span>
                  <span className="text-xs text-white/70">{currentFeature.episodes}集</span>
                </div>
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3 leading-tight">
                  {currentFeature.title}
                </h1>
                <p className="text-sm md:text-base text-white/80 mb-4 max-w-lg line-clamp-2">
                  {currentFeature.description}
                </p>
                <div className="flex items-center gap-2 mb-5">
                  <span className="flex items-center gap-1 text-accent-400 font-semibold text-lg">
                    <i className="ri-star-fill"></i>
                    {currentFeature.rating}
                  </span>
                  <span className="text-white/60 text-sm">{currentFeature.year}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Link
                    to={`/detail/${currentFeature.id}`}
                    className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    立即观看
                  </Link>
                  <Link
                    to="/category/all"
                    className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all backdrop-blur-sm whitespace-nowrap"
                  >
                    <i className="ri-apps-2-line text-base"></i>
                    浏览全部
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-4 md:bottom-6 left-0 right-0 flex justify-center gap-2">
            {featuredSeries.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentBanner(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentBanner
                    ? 'bg-primary-500 w-6'
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`切换到第${i + 1}张`}
              />
            ))}
          </div>

          <button
            onClick={prevBanner}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-sm transition-all"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            onClick={nextBanner}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-sm transition-all"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </section>

        {/* Categories */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                分类浏览
              </h2>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50 hover:bg-primary-500 transition-all"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-background-50/20 group-hover:text-background-50 transition-all">
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </div>
                  <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-background-50 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-[10px] md:text-xs text-foreground-400 group-hover:text-background-50/70 transition-colors">
                    {cat.count}部
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Series */}
        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                <i className="ri-fire-line text-primary-500 mr-2"></i>
                热门推荐
              </h2>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                更多热剧 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {hotSeries.map((s) => (
                <TVSeriesCard key={s.id} series={s} />
              ))}
            </div>
          </div>
        </section>

        {/* Banner CTA */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20living%20room%20with%20large%20TV%20screen%20displaying%20colorful%20abstract%20visuals%2C%20cozy%20atmosphere%20with%20soft%20ambient%20lighting%2C%20modern%20minimalist%20interior%2C%20comfortable%20sofa%2C%20entertainment%20theme%2C%20high%20quality%20production%20design&width=1600&height=500&seq=home-cta-bg&orientation=landscape"
            alt="天美影视追剧体验"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 text-center px-4">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
              随时随地，畅享追剧
            </h2>
            <p className="text-white/80 text-sm md:text-base mb-6 max-w-lg mx-auto">
              海量高清电视剧资源，流畅不卡顿，完全免费，打开即看
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              开始追剧
            </Link>
          </div>
        </section>

        {/* Latest + Top Rated */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    最新上线
                  </h2>
                  <Link
                    to="/category/all"
                    className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    全部新剧 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {latestSeries.map((s) => (
                    <TVSeriesCard key={s.id} series={s} variant="compact" />
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    高分神作
                  </h2>
                  <Link
                    to="/category/all"
                    className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    更多 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="space-y-3">
                  {topRated.map((s, i) => (
                    <Link
                      key={s.id}
                      to={`/detail/${s.id}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-background-50 hover:bg-background-200 transition-all group"
                    >
                      <span className="font-heading text-lg font-bold text-foreground-300 w-6 text-center">
                        {i + 1}
                      </span>
                      <div className="w-10 h-14 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={s.cover}
                          alt={`${s.title} 天美影视`}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {s.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-foreground-500">
                          <span className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                            {s.rating}
                          </span>
                          <span>{s.year}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqData.map((faq, i) => (
                <details
                  key={i}
                  className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-base flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          data-jsonld="faq"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqData.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}